import React, { useState, useEffect, useRef } from 'react';
import { Store } from './model';
import { useMediaQuery } from 'react-responsive';

export interface CarouselProps {
    store: Store[];
}

const Carousel: React.FC<CarouselProps> = ({ store }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const isSmallScreen = useMediaQuery({ query: '(max-width: 768px)' });
    const [isAnimating, setIsAnimating] = useState(false);
    const startX = useRef(0);
    const endX = useRef(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const handleTouchStart = (e: React.TouchEvent) => {
        startX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        endX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        const distance = startX.current - endX.current;
        if (distance > 50) {
            nextSlide();
        } else if (distance < -50) {
            prevSlide();
        }
    };

    const prevSlide = () => {
        if (!isAnimating) {
            setIsAnimating(true);
            setCurrentIndex(prevIndex => (prevIndex === 0 ? store.length - 1 : prevIndex - 1));
            setIsAnimating(false);
            resetInterval();
        }
    };

    const nextSlide = () => {
        if (!isAnimating) {
            setIsAnimating(true);
            setCurrentIndex(prevIndex => (prevIndex === store.length - 1 ? 0 : prevIndex + 1));
            setIsAnimating(false);
            resetInterval();
        }
    };

    const resetInterval = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        intervalRef.current = setInterval(() => {
            setIsAnimating(true);
            setCurrentIndex(prevIndex => (prevIndex === store.length - 1 ? 0 : prevIndex + 1));
            setIsAnimating(false);
        }, 7000);
    };

    useEffect(() => {
        resetInterval();
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [store.length]);

    if (store.length === 0) {
        return <div>No data available</div>;
    }

    return (
        <div
            className="relative max-w-[1196px] overflow-hidden font-pretendard"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
                {store.map((item, index) => (
                    <div key={index} className="w-full flex-shrink-0">
                        <img
                            src={item.image}
                            alt={`Slide ${index}`}
                            className="w-full h-full object-cover"
                        />
                    </div>
                ))}
            </div>
            <div className="relative md:h-[250px] h-[200px] pt-12 mt-2 flex justify-center">
                <div className="text-center text-livos-green">
                    <a href={store[currentIndex].mapurl}>
                        <p className="md:text-[50px] text-[20px] font-medium flex items-center justify-center">
                            {Array.from(store[currentIndex].title).map((char, idx) => {
                                if (char === " ") {
                                    return (
                                        <span key={idx} className="font-pretendard">
                                            {"\u00A0"}
                                        </span>
                                    );
                                }
                                if (/[A-Za-z]/.test(char)) {
                                    return (
                                        <span key={idx} className="font-teachers md:text-[50px] text-[20px]">
                                            {char}
                                        </span>
                                    );
                                }

                                if (/[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(char)) {
                                    return (
                                        <span key={idx} className="font-pretendard md:text-[40px] text-[20px]">
                                            {char}
                                        </span>
                                    );
                                }

                                return (
                                    <span key={idx} className="font-pretendard md:text-[40px] text-[20px]">
                                        {char}
                                    </span>
                                );
                            })}

                            <img
                                src="/assets/images/location/link_icon.svg"
                                alt="Link"
                                className="ml-4 w-[15px] h-[15px] md:h-[30px] md:w-[30px]"
                            />
                        </p>
                    </a>
                    <div className="leading-[15px] md:leading-normal">
                        <p className="font-crimson text-[13px] md:text-[22px] font-normal">
                            {store[currentIndex].location}
                        </p>
                        <p className="font-crimson text-[13px] md:text-[22px] font-normal">
                            {store[currentIndex].phone}
                        </p>

                        <p className="font-crimson text-[13px] md:text-[22px] font-normal whitespace-pre-line">
                            {store[currentIndex].description}
                        </p>
                    </div>

                </div>
                {!isSmallScreen && (
                    <>
                        <div className="absolute inset-y-0 left-0 flex items-center">
                            <button onClick={prevSlide} className="hover:scale-100 transform transition-none">
                                <img src="/assets/images/location/left_button.svg" alt="Previous" />
                            </button>
                        </div>
                        <div className="absolute inset-y-0 right-0 flex items-center">
                            <button onClick={nextSlide} className="hover:scale-100 transform transition-none">
                                <img src="/assets/images/location/right_button.svg" alt="Next" />
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div >
    );
};

export default Carousel;
